import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useStore } from '@/hooks/useStore';
import { listLimitRequestsByFarmer, cancelLimitRequest, type BackendLimitRequest } from '@/api/lending';
import {
  getContractsByFarmer,
  getFarmerById,
  getFarmerKyc,
  getLimitsByFarmer,
  getSeasonsByFarmer,
  invalidateContractsForFarmer,
} from '@/store';
import { EmptyState } from '@/components/base/EmptyState';
import { formatDate } from '@/utils/format';
import { formatCompactCurrency } from '@/utils/format';
import { getFarmerMoneySummary } from '@/store';
import { ApplyLimitSheet } from '@/pages/farmer/info/components/ApplyLimitSheet';
import { confirmContract } from '@/api/contract';
import { showToast } from '@/components/base/Toast';
import { confirmModal } from '@/components/base/ConfirmModal';
import type { Contract } from '@/types';

export default function FarmerLimitPage() {
  useStore();
  const { user } = useAuth();
  const farmerId = user?.farmerId || 'f1';
  const farmer = getFarmerById(farmerId);
  const kyc = farmer ? getFarmerKyc(farmer) : null;
  const [limitOpen, setLimitOpen] = useState(false);
  const [detailContract, setDetailContract] = useState<Contract | null>(null);
  // A submitted request has to be visible somewhere, or "gửi rồi mà không biết đi
  // đâu" is still true even once the record exists server-side.
  const [limitRequests, setLimitRequests] = useState<BackendLimitRequest[]>([]);

  const loadRequests = useCallback(() => {
    if (!user?.farmerId) return;
    listLimitRequestsByFarmer(user.farmerId)
      .then(setLimitRequests)
      // Not fatal: the rest of the page stands on its own.
      .catch(() => setLimitRequests([]));
  }, [user?.farmerId]);

  useEffect(loadRequests, [loadRequests]);

  const contracts = getContractsByFarmer(farmerId);
  const seasons = getSeasonsByFarmer(farmerId);
  const limits = getLimitsByFarmer(farmerId);
  // Read through, not useMemo. Every other store read on this page already does;
  // this one was keyed on [farmerId], so it pinned the zeroes returned before the
  // limit fetch landed and the page kept showing "Tổng hạn mức 0 / Còn khả dụng 0"
  // for the rest of the session, even though useStore() had re-rendered it.
  const moneySummary = getFarmerMoneySummary(farmerId);

  const contractName = (contractId?: string) => contracts.find((contract) => contract.id === contractId)?.soHopDong || 'Hợp đồng bao tiêu';

  return (
    <div className="mx-auto max-w-[800px] pb-4">
      <div className="mx-4 mt-4 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase text-slate-400">Nông hộ</p>
            <h2 className="text-lg font-bold text-slate-900">Hạn mức & hợp đồng</h2>
          </div>
          <button
            type="button"
            onClick={() => setLimitOpen(true)}
            className="flex min-h-11 items-center gap-1 rounded-xl bg-agrida-600 px-3 text-xs font-bold text-white shadow-lg shadow-agrida-600/15"
          >
            <i className="ri-scales-3-line" />
            Xin hạn mức
          </button>
        </div>

        <section className="grid grid-cols-2 gap-2">
          <LimitMetric label="Tổng hạn mức" value={formatCompactCurrency(moneySummary.hanMucTienTong)} />
          <LimitMetric label="Còn khả dụng" value={formatCompactCurrency(moneySummary.hanMucTienKhaDung)} tone="emerald" />
          <LimitMetric label="Đã sử dụng" value={formatCompactCurrency(moneySummary.hanMucTienDaSuDungThucTe)} tone="amber" />
          <LimitMetric label="Đang khóa" value={formatCompactCurrency(moneySummary.lockedAmount || 0)} tone="rose" />
        </section>

        {limitRequests.length > 0 && (
          <section>
            <h3 className="mb-2 text-sm font-semibold text-slate-900">Yêu cầu hạn mức đã gửi</h3>
            <div className="space-y-2">
              {limitRequests.map((req) => {
                const tone = req.status === 'APPROVED'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : req.status === 'REJECTED'
                    ? 'bg-red-50 text-red-700 border-red-200'
                    : req.status === 'CANCELLED'
                      ? 'bg-slate-100 text-slate-500 border-slate-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200';
                const label = req.status === 'APPROVED' ? 'Đã duyệt' : req.status === 'REJECTED' ? 'Từ chối' : req.status === 'CANCELLED' ? 'Đã hủy' : 'Chờ duyệt';
                const handleCancelRequest = async () => {
                  const ok = await confirmModal({
                    title: 'Hủy yêu cầu hạn mức này?',
                    message: 'Bạn có thể gửi yêu cầu mới cho hợp đồng này sau khi hủy.',
                    confirmText: 'Hủy yêu cầu',
                    cancelText: 'Đóng',
                    variant: 'danger',
                  });
                  if (!ok) return;
                  try {
                    await cancelLimitRequest(req._id);
                    showToast('Đã hủy yêu cầu hạn mức', 'success');
                    loadRequests();
                  } catch (e) {
                    showToast(e instanceof Error ? e.message : 'Hủy yêu cầu thất bại', 'error');
                  }
                };
                return (
                  <article key={req._id} data-testid="limit-request-row" className="rounded-xl border border-slate-200 bg-white p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-slate-900">{formatCompactCurrency(Number(req.requestedAmountVnd))}</p>
                        <p className="mt-0.5 text-[11px] text-slate-500">
                          {contractName(req.contractId)}
                          {req.createdAt ? ` · ${new Date(req.createdAt).toLocaleDateString('vi-VN')}` : ''}
                        </p>
                      </div>
                      <span className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-semibold ${tone}`}>{label}</span>
                    </div>
                    {req.reason && <p className="mt-1.5 text-[11px] text-slate-500">Lý do: {req.reason}</p>}
                    {req.decisionNote && <p className="mt-1 text-[11px] text-slate-600">Phản hồi: {req.decisionNote}</p>}
                    {/* Feedback 08 B1: only whoever filed the request may cancel it
                        (backend enforces this) — an HTX-created request showed this
                        button to the farmer anyway, who then hit a 403 on click. */}
                    {req.status === 'PENDING' && req.requestedBy === user?.id && (
                      <button
                        type="button"
                        onClick={handleCancelRequest}
                        className="mt-2 text-[11px] font-semibold text-red-500 hover:text-red-600"
                      >
                        Hủy yêu cầu
                      </button>
                    )}
                    {req.status === 'PENDING' && req.requestedBy && req.requestedBy !== user?.id && (
                      <p className="mt-2 text-[11px] text-slate-400">HTX đã gửi yêu cầu này — liên hệ HTX nếu cần hủy.</p>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        )}

        <section>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Hạn mức theo hợp đồng</h3>
            <span className="text-[10px] text-slate-400">{limits.length} hạn mức</span>
          </div>
          {limits.length === 0 ? (
            <EmptyState icon="ri-scales-3-line" title="Chưa có hạn mức" description="HTX sẽ cấp hạn mức dựa trên hợp đồng bao tiêu." />
          ) : (
            <div className="space-y-2">
              {limits.map((limit) => (
                <article key={limit.id} className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h4 className="truncate text-sm font-bold text-slate-900">{contractName(limit.contractId)}</h4>
                      <p className="mt-0.5 text-[11px] text-slate-500">{limit.loaiDinhMuc} · {limit.nam}</p>
                    </div>
                    <span className="rounded-full bg-agrida-100 px-2 py-1 text-[10px] font-semibold text-agrida-700">Đang hiệu lực</span>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <LimitMini label="Đã cấp" value={formatCompactCurrency(limit.hanMuc)} />
                    <LimitMini label="Đã dùng" value={formatCompactCurrency(limit.daSuDungThucTe ?? limit.daSuDung ?? 0)} tone="amber" />
                    <LimitMini label="Còn lại" value={formatCompactCurrency(Math.max(0, limit.hanMuc - (limit.daSuDung || 0)))} tone="emerald" />
                  </div>
                  {!!limit.dangKhoa && (
                    <p className="mt-1.5 text-[10px] text-slate-400">Đang khóa (chờ giải ngân): {formatCompactCurrency(limit.dangKhoa)}</p>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Hợp đồng của tôi</h3>
            <span className="text-[10px] text-slate-400">{contracts.length} hợp đồng</span>
          </div>
          <div className="space-y-2">
            {contracts.map((contract) => {
              const linkedSeasons = seasons.filter((season) => contract.seasonIds?.includes(season.id));
              return (
                <button
                  key={contract.id}
                  type="button"
                  onClick={() => setDetailContract(contract)}
                  className="w-full rounded-xl border border-slate-200 bg-white p-3 text-left"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h4 className="truncate text-sm font-bold text-slate-900">{contract.soHopDong}</h4>
                      <p className="mt-0.5 line-clamp-2 text-[11px] text-slate-500">{contract.noiDung}</p>
                    </div>
                    {/* Feedback 08 A2: the real status must stay visible even when
                        expiringSoon is also true — a badge that only ever shows one
                        or the other used to hide "Đang hiệu lực" the moment a
                        contract neared its expiry date. */}
                    <div className="flex shrink-0 items-center gap-1">
                      <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${contract.status === 'Đang hiệu lực' ? 'bg-agrida-100 text-agrida-700' : contract.status === 'Nháp' ? 'bg-slate-100 text-slate-600' : contract.status === 'Sắp hết hạn' || contract.status === 'Hết hạn' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{contract.status}</span>
                      {contract.expiringSoon && contract.status === 'Đang hiệu lực' && (
                        <span className="rounded-full bg-amber-100 px-2 py-1 text-[10px] font-semibold text-amber-700">Sắp hết hạn</span>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <LimitMini label="Giá trị" value={formatCompactCurrency(contract.giaTri)} />
                    <LimitMini label="Hạn mức đã cấp" value={formatCompactCurrency(contract.hanMucDaCap || 0)} tone="emerald" />
                  </div>
                  {linkedSeasons.length > 0 && (
                    <p className="mt-2 line-clamp-1 text-[10px] text-slate-400">Mùa vụ: {linkedSeasons.map((season) => season.name).join(', ')}</p>
                  )}
                </button>
              );
            })}
          </div>
        </section>

      </div>

      {farmer && (
        <ApplyLimitSheet
          open={limitOpen}
          onClose={() => setLimitOpen(false)}
          farmerId={farmerId}
          farmerName={farmer.name}
          onSubmitted={loadRequests}
        />
      )}

      {detailContract && (
        <ContractDetailModal
          contract={detailContract}
          seasons={seasons.filter((season) => detailContract.seasonIds?.includes(season.id))}
          onClose={() => setDetailContract(null)}
          onSigned={() => invalidateContractsForFarmer(farmerId)}
        />
      )}
    </div>
  );
}

function LimitMetric({ label, value, tone = 'slate' }: { label: string; value: string; tone?: 'slate' | 'emerald' | 'amber' | 'rose' }) {
  const color = tone === 'emerald' ? 'text-agrida-700' : tone === 'amber' ? 'text-amber-700' : tone === 'rose' ? 'text-rose-700' : 'text-slate-900';
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="text-[10px] font-semibold uppercase text-slate-400">{label}</p>
      <p className={`mt-1 text-base font-bold ${color}`}>{value}</p>
    </div>
  );
}

function LimitMini({ label, value, tone = 'slate' }: { label: string; value: string; tone?: 'slate' | 'emerald' | 'amber' }) {
  const color = tone === 'emerald' ? 'text-agrida-700' : tone === 'amber' ? 'text-amber-700' : 'text-slate-900';
  return (
    <div className="rounded-xl bg-slate-50 p-2">
      <p className="text-[10px] text-slate-400">{label}</p>
      <p className={`mt-0.5 truncate text-xs font-bold ${color}`}>{value}</p>
    </div>
  );
}

function ContractDetailModal({ contract, seasons, onClose, onSigned }: { contract: Contract; seasons: { id: string; name: string }[]; onClose: () => void; onSigned?: () => void }) {
  const [busy, setBusy] = useState(false);

  const needsSignature = contract.status === 'Chờ ký';

  // No OTP — being logged into the app already is the verification, so
  // confirming is a single authenticated tap.
  const handleConfirm = async () => {
    const ok = await confirmModal({
      title: 'Xác nhận hợp đồng',
      message: 'Bạn xác nhận đã đọc và đồng ý với nội dung hợp đồng này?',
      confirmText: 'Xác nhận',
      cancelText: 'Để sau',
      variant: 'primary',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await confirmContract(contract.id);
      showToast('Đã xác nhận hợp đồng', 'success');
      onSigned?.();
      onClose();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Xác nhận thất bại', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[85] flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} />
      <div className="relative max-h-[90dvh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-w-md sm:rounded-2xl">
        <div className="sticky top-0 flex items-center justify-between border-b border-slate-100 bg-white px-4 py-3">
          <h3 className="text-base font-bold text-slate-900">Chi tiết hợp đồng</h3>
          <button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100">
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <div className="space-y-3 p-4">
          <div>
            <p className="text-xs text-slate-400">Số hợp đồng</p>
            <p className="font-bold text-slate-900">{contract.soHopDong}</p>
          </div>
          <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">{contract.noiDung}</div>
          {needsSignature && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-800">Hợp đồng đang chờ bạn xác nhận</p>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={busy}
                className="mt-2 w-full rounded-xl bg-amber-600 py-2 text-xs font-semibold text-white disabled:opacity-50"
              >
                {busy ? 'Đang xác nhận...' : 'Xác nhận hợp đồng'}
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <LimitMini label="Giá trị" value={formatCompactCurrency(contract.giaTri)} />
            <LimitMini label="Đã cấp hạn mức" value={formatCompactCurrency(contract.hanMucDaCap || 0)} tone="emerald" />
            <LimitMini label="Hiệu lực" value={formatDate(contract.ngayHieuLuc)} />
            <LimitMini label="Hết hạn" value={formatDate(contract.ngayHetHan)} />
          </div>
          {seasons.length > 0 && (
            <div className="rounded-xl border border-slate-200 p-3">
              <p className="text-[10px] font-semibold uppercase text-slate-400">Mùa vụ trong hợp đồng</p>
              <p className="mt-1 text-sm font-semibold text-slate-800">{seasons.map((season) => season.name).join(', ')}</p>
            </div>
          )}
          <div className="rounded-xl border border-slate-200 p-3">
            <p className="text-[10px] font-semibold uppercase text-slate-400">Tài liệu đính kèm</p>
            {contract.attachments && contract.attachments.length > 0 ? (
              <div className="mt-2 space-y-2">
                {contract.attachments.map((file) => (
                  <div key={file.fileId} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                    <span className="truncate text-xs font-semibold text-slate-700">{file.caption || (file.type === 'image' ? 'Ảnh đính kèm' : 'Tài liệu')}</span>
                    <i className="ri-file-pdf-2-line text-rose-500" />
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-xs text-slate-500">Chưa có tài liệu đính kèm trong dữ liệu mô phỏng.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
